import { useCallback, useEffect, useRef } from 'react';

/**
 * Form-draft recovery buffer (F100/A12). Persists a form's values to
 * **sessionStorage** so a JWT expiry mid-typing (which logs the user out and
 * redirects to /login) costs at most a relogin — never the typed work.
 *
 * Why sessionStorage, not localStorage: the draft must die when the tab closes.
 * It is a short-lived recovery buffer (default 30min TTL), not durable storage.
 *
 * Keys are namespaced ``draft:<key>`` and callers SHOULD scope ``key`` by the
 * authenticated ``userId`` so a shared browser never restores one account's
 * draft after a user switch (F100 Multi-Tenancy note).
 *
 * The debounced ``save`` avoids a write per keystroke; ``auth:unauthorized`` and
 * ``beforeunload`` listeners ``flush`` the last pending value so the final
 * keystrokes between two debounce ticks aren't lost when the session expires.
 */

interface DraftEnvelope<T> {
  savedAt: number;
  values: T;
}

interface UseDraftOptions {
  /** How long a draft stays restorable (ms). Default 30min. */
  ttlMs?: number;
  /** Debounce before a ``save`` actually writes (ms). Default 500. */
  debounceMs?: number;
}

interface UseDraftReturn<T> {
  /** Debounced persist of the current form values. */
  save: (values: T) => void;
  /** Returns the stored values if within TTL, else null (and purges expired). */
  restore: () => T | null;
  /** Remove the persisted draft (call on a successful submit). */
  clear: () => void;
  /** Immediately write the last pending value (bypasses the debounce). */
  flush: () => void;
}

const DEFAULT_TTL_MS = 30 * 60 * 1000;

export function useDraft<T>(
  key: string,
  { ttlMs = DEFAULT_TTL_MS, debounceMs = 500 }: UseDraftOptions = {}
): UseDraftReturn<T> {
  const storageKey = `draft:${key}`;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<T | null>(null);

  const writeNow = useCallback(
    (values: T) => {
      try {
        const envelope: DraftEnvelope<T> = { savedAt: Date.now(), values };
        sessionStorage.setItem(storageKey, JSON.stringify(envelope));
      } catch {
        // sessionStorage can throw (quota / privacy mode) — a lost draft is a
        // recoverable annoyance, never a crash.
      }
    },
    [storageKey]
  );

  const clearTimer = () => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  };

  const save = useCallback(
    (values: T) => {
      pending.current = values;
      clearTimer();
      if (debounceMs <= 0) {
        writeNow(values);
        pending.current = null;
        return;
      }
      timer.current = setTimeout(() => {
        writeNow(values);
        pending.current = null;
      }, debounceMs);
    },
    [debounceMs, writeNow]
  );

  const flush = useCallback(() => {
    clearTimer();
    if (pending.current !== null) {
      writeNow(pending.current);
      pending.current = null;
    }
  }, [writeNow]);

  const clear = useCallback(() => {
    clearTimer();
    pending.current = null;
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore — see writeNow
    }
  }, [storageKey]);

  const restore = useCallback((): T | null => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(storageKey);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const env = JSON.parse(raw) as DraftEnvelope<T>;
      if (typeof env.savedAt !== 'number' || Date.now() - env.savedAt > ttlMs) {
        clear();
        return null;
      }
      return env.values;
    } catch {
      clear();
      return null;
    }
  }, [storageKey, ttlMs, clear]);

  // Flush the last pending value when the session expires or the tab closes so
  // the keystrokes between two debounce ticks survive (F100 Notes).
  useEffect(() => {
    const onFlush = () => flush();
    window.addEventListener('auth:unauthorized', onFlush);
    window.addEventListener('beforeunload', onFlush);
    return () => {
      window.removeEventListener('auth:unauthorized', onFlush);
      window.removeEventListener('beforeunload', onFlush);
      clearTimer();
    };
  }, [flush]);

  return { save, restore, clear, flush };
}
